import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatEUR, formatDate, daysUntil, auth } from "../lib/api";
import Layout, { Card, Label, Badge, Spinner } from "../components/Layout";
import { HelpInline } from "../components/HelpAndSearch";
import { Globe, Download, Translate, CheckSquare, Eye, ArrowsClockwise, LinkSimple } from "@phosphor-icons/react";

const CPV_OPTIONS = [
  { code: "72000000", label: "Servizi IT" },
  { code: "45000000", label: "Costruzioni" },
  { code: "85000000", label: "Sanita" },
  { code: "80000000", label: "Istruzione e formazione" },
  { code: "75100000", label: "Servizi PA / anticorruzione" },
  { code: "73000000", label: "R&D" },
  { code: "71000000", label: "Architettura, ingegneria" },
  { code: "79000000", label: "Servizi alle imprese" },
];

export default function EUTenders() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [translating, setTranslating] = useState({});
  const [cpv, setCpv] = useState("72000000");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/tenders?portal=TED");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const scrape = async () => {
    setScraping(true);
    try {
      const { data } = await api.post("/scrapers/ted", { cpv, limit: 15 });
      alert(`Importate ${data.created} nuove gare europee TED reali. ${data.skipped || 0} gia presenti. Scaricate alle ${data.queried_at?.slice(11, 19)}`);
      load();
    } catch (e) { alert("Errore: " + e.message); }
    finally { setScraping(false); }
  };

  const scrapeBatch = async () => {
    setScraping(true);
    try {
      const { data } = await api.post("/scrapers/ted-batch");
      alert(`Batch multi-CPV completato: ${data.total_created} nuove gare reali importate da TED EU.`);
      load();
    } catch (e) { alert("Errore: " + e.message); }
    finally { setScraping(false); }
  };

  const translate = async (tid) => {
    setTranslating({ ...translating, [tid]: true });
    try {
      await api.post(`/tenders/${tid}/translate-ita`);
      load();
    } catch (e) { alert("Errore traduzione: " + (e.response?.data?.detail || e.message)); }
    finally { setTranslating({ ...translating, [tid]: false }); }
  };

  const toggleReview = async (tid) => {
    try { await api.post(`/tenders/${tid}/under-review`); load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const myName = auth.getUser()?.name;

  return (
    <Layout
      title="Gare Europee (TED)"
      subtitle="Bandi europei reali importati dall'API ufficiale Tenders Electronic Daily — gestione consorzio UE, traduzione IT, eForms"
      actions={
        <>
          <select value={cpv} onChange={(e) => setCpv(e.target.value)} className="input-field text-xs max-w-[220px]" data-testid="cpv-select">
            {CPV_OPTIONS.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.label}</option>)}
          </select>
          <button onClick={scrape} disabled={scraping} className="btn-secondary" data-testid="btn-scrape-cpv">
            <Download size={14} className="inline mr-1.5" /> Scarica CPV
          </button>
          <button onClick={scrapeBatch} disabled={scraping} className="btn-primary" data-testid="btn-scrape-batch">
            <ArrowsClockwise size={14} className="inline mr-1.5" /> Scarica tutti (6 CPV)
          </button>
          <HelpInline page="eu_tenders" />
        </>
      }
    >
      <Card className="mb-4 ai-panel">
        <div className="flex items-start gap-4">
          <Globe weight="duotone" className="text-violet-600 flex-shrink-0" size={24} />
          <div>
            <Label>Workflow Gare UE</Label>
            <p className="text-sm text-zinc-700 mt-1">
              Le gare TED hanno un iter dedicato: <strong>eForms compliance</strong>, possibile <strong>multilingua</strong>,
              richiesta tipica di <strong>consorzio multi-paese</strong>. Dal dettaglio della gara puoi:
              attivare <strong>EU Partner Finder</strong>, eseguire <strong>Deep EU Analysis</strong>, generare offerta in lingua appropriata.
              Lo scheduler automatico scarica nuove gare TED <strong>ogni ora</strong>.
            </p>
          </div>
        </div>
      </Card>

      {loading ? <Spinner /> :
       items.length === 0 ? (
         <Card><div className="text-sm text-zinc-500 py-8 text-center">Nessuna gara TED ancora scaricata. Clicca "Scarica tutti" per iniziare.</div></Card>
       ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm" data-testid="eu-tenders-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <Th>Gara (TED)</Th>
                <Th>Importo</Th>
                <Th>Lingua</Th>
                <Th>Scaricata il</Th>
                <Th>In carico a</Th>
                <Th>Vista da</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => {
                const isMine = t.under_review_by === myName;
                const others = t.under_review && !isMine;
                const neverViewed = !(t.viewed_by && t.viewed_by.length);
                return (
                  <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`eu-row-${t.id}`}>
                    <td className="px-4 py-3 max-w-md cursor-pointer" onClick={() => nav(`/tenders/${t.id}`)}>
                      <div className="font-medium truncate flex items-center gap-2">
                        {t.title}
                        {neverViewed && <Badge kind="contendibile">NUOVA</Badge>}
                      </div>
                      <div className="text-xs text-zinc-500 font-mono mt-0.5 flex items-center gap-2">
                        TED {t.cig}
                        {t.url && (
                          <a href={t.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-klein hover:underline inline-flex items-center gap-0.5">
                            <LinkSimple size={11} /> portale
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-right">{formatEUR(t.base_amount)}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge kind={t.translated || t.original_language === "ita" ? "aperta" : "default"}>
                        {t.original_language || "?"}
                      </Badge>
                      {!t.translated && t.original_language !== "ita" && (
                        <button onClick={() => translate(t.id)} disabled={translating[t.id]} className="block mt-1 text-[10px] text-klein hover:underline" data-testid={`translate-${t.id}`}>
                          {translating[t.id] ? "..." : <><Translate size={10} className="inline mr-0.5" /> Traduci IT</>}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="font-mono">{t.scraped_at ? new Date(t.scraped_at).toLocaleString("it-IT") : "—"}</div>
                      <div className="text-[10px] text-zinc-500">scadenza: {formatDate(t.deadline_at)}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {t.under_review_by ? (
                        <div className={isMine ? "text-emerald-700 font-semibold" : "text-amber-700"}>
                          <CheckSquare size={12} weight="fill" className="inline mr-1" />
                          {t.under_review_by} {isMine && "(tu)"}
                        </div>
                      ) : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {t.viewed_by && t.viewed_by.length > 0 ? (
                        <>
                          <div className="font-medium">{t.last_viewed_by || t.viewed_by[0]?.user_name}</div>
                          <div className="text-[10px] text-zinc-500">
                            <Eye size={10} className="inline mr-0.5" /> {t.viewed_by.length} {t.viewed_by.length === 1 ? "vista" : "viste"}
                          </div>
                        </>
                      ) : <span className="text-zinc-400">mai</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => toggleReview(t.id)} disabled={others} className="btn-ghost text-xs" data-testid={`review-${t.id}`} title={others ? `Presa da ${t.under_review_by}` : ""}>
                        {isMine ? "Rilascia" : "Prendi in esame"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </Layout>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-left label-up text-zinc-600">{children}</th>;
}
